/* eslint-disable @typescript-eslint/no-explicit-any -- Permission matrices merge dynamic JSONB from multiple sources. */

import { isAdminRole } from '@/lib/auth-core';
import { isTourVideoCaptureMode } from '@/lib/tourCaptureMode';

export const OPERATIONS_ACTION_KEYS = [
  'smart_task', // Controls whether the item type appears in Smart Task modals (vs admin-only)
  'create',
  'resolve',
  'close',
  'admin_reopen',
  'reopen',
  'delete',
  'print', // Added print/report action for RBAC
  'view', // Added view permission for team member confidentiality RBAC
  'edit', // Added edit permission for team member confidentiality RBAC
  'edit_comments', // Edit, reply to, and delete OTHER users' comments on work items (admin portal / WorkItemDetailModal); own comments are always manageable
  'edit_timers', // Add, edit, and delete time entries on work items (admin portal / WorkItemDetailModal)
  'edit_photos', // Edit captions, captured times, visibility, and delete OTHER users' photos (admin portal / WorkItemDetailModal photos tab); own photos are always manageable
  'submit_for_billing', // Submit a job/project for billing conversion
  'review_billing', // Review billing calculations (Standard/Strict modes)
  'approve_billing', // Approve billing for finance conversion (Strict mode)
  // ── Workflow-specific action keys (used by the Actions tab for SmartTask) ──
  'issue_to_van', // Issue stock from warehouse to technician mobile warehouse
  'issue_to_job', // Consume parts from van onto a service job
  'return_to_warehouse', // Return unused parts from van back to warehouse
  'receive_stock', // Receive stock into warehouse from suppliers or transfers
  'stock_count', // Perform stock counts, cycle counts, and inventory audits
  'manage_equipment', // Check out, check in, and inspect tools and equipment
  // ── Fleet management action keys ──
  'create_inspection', // Complete/submit vehicle inspections
  'override_inspection', // Admin authorizes inspection skip/override
  'change_vehicle', // Change vehicle mid-shift
  'handover_vehicle', // Hand over vehicle to fleet or another driver
  'log_service', // Log service/maintenance entries
  'log_fuel', // Log fuel entries
  'assign_driver', // Assign/unassign drivers to vehicles
  'manage_license', // Add/delete vehicle licenses and certificates
  'acknowledge_inspection', // Acknowledge AI inspection reviews and create corrective tasks
  'run_ai_review', // Trigger AI analysis of inspection photos
  'manage_fleet_settings', // Manage fleet-level configuration and provider credentials
  // ── Total Forms action keys ──
  'submit', // Submit form responses
  'publish', // Publish forms, templates, or marketplace listings
  'share', // Share items via public links or embed codes
  'assign', // Assign items to clients, sites, or users
  'review', // Review and mark submissions as reviewed
  // ── Generic approval key ──
  // Approval authority on request/record item types — used by the
  // time-attendance workflow matrix (overtime/leave/timesheets rows) so
  // ops admins can be granted approval rights per surface without full
  // module access.
  'approve', // Approve or reject requests (leave, overtime, timesheets)
] as const;

export type OperationsActionKey = typeof OPERATIONS_ACTION_KEYS[number];
export type OperationsActionMap = Partial<Record<OperationsActionKey, boolean>>;

export interface OperationsActionPermissions {
  item_types: Record<string, OperationsActionMap>;
  /**
   * Per-workflow matrix slices. Permission groups created for a specific
   * workflow (permission_groups.source_workflow_key set) merge their entire
   * Actions tab matrix into `workflows[workflowKey]` — so e.g.
   * fleet-management's `task` row can never bleed into the service-operations
   * modal, and one workflow's `view`/`create` cells can't override another
   * workflow's. Rows in `item_types` (from non-workflow groups and per-user
   * custom_permissions) act as global defaults consulted when the workflow
   * slice has no explicit cell.
   */
  workflows?: Record<string, { item_types: Record<string, OperationsActionMap> }>;
}

function toPlainObject<T extends Record<string, any>>(value: unknown): T | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as T;
}

export function normalizeOperationsActionItemType(itemType: unknown): string {
  return typeof itemType === 'string'
    ? itemType.trim().toLowerCase().replace(/[\s-]+/g, '_')
    : '';
}

export function formatOperationsActionItemTypeLabel(itemType: string): string {
  return normalizeOperationsActionItemType(itemType)
    .split('_')
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function normalizeItemTypeActionMap(
  rawItemTypes: Record<string, any> | null,
): Record<string, OperationsActionMap> {
  if (!rawItemTypes) {
    return {};
  }

  return Object.entries(rawItemTypes).reduce<Record<string, OperationsActionMap>>((acc, [rawItemType, rawActions]) => {
    const itemType = normalizeOperationsActionItemType(rawItemType);
    const actionMap = toPlainObject<Record<string, any>>(rawActions);

    if (!itemType || !actionMap) {
      return acc;
    }

    const normalizedActions = OPERATIONS_ACTION_KEYS.reduce<OperationsActionMap>((actionAcc, actionKey) => {
      if (typeof actionMap[actionKey] === 'boolean') {
        actionAcc[actionKey] = actionMap[actionKey];
      }
      return actionAcc;
    }, {});

    if (Object.keys(normalizedActions).length > 0) {
      acc[itemType] = normalizedActions;
    }

    return acc;
  }, {});
}

export function normalizeOperationsActionPermissions(value: unknown): OperationsActionPermissions {
  const rawValue = toPlainObject<Record<string, any>>(value);
  const result: OperationsActionPermissions = {
    item_types: normalizeItemTypeActionMap(toPlainObject<Record<string, any>>(rawValue?.item_types)),
  };

  const rawWorkflows = toPlainObject<Record<string, any>>(rawValue?.workflows);
  if (rawWorkflows) {
    const workflows: NonNullable<OperationsActionPermissions['workflows']> = {};
    for (const [rawWorkflowKey, rawWorkflowValue] of Object.entries(rawWorkflows)) {
      const workflowKey = normalizeOperationsActionItemType(rawWorkflowKey).replace(/_/g, '-');
      const itemTypes = normalizeItemTypeActionMap(
        toPlainObject<Record<string, any>>(toPlainObject<Record<string, any>>(rawWorkflowValue)?.item_types),
      );
      if (workflowKey && Object.keys(itemTypes).length > 0) {
        workflows[workflowKey] = { item_types: itemTypes };
      }
    }
    if (Object.keys(workflows).length > 0) {
      result.workflows = workflows;
    }
  }

  return result;
}

export function isOperationsActionPermissionsEmpty(value: unknown): boolean {
  const normalized = normalizeOperationsActionPermissions(value);
  return Object.keys(normalized.item_types).length === 0
    && Object.keys(normalized.workflows ?? {}).length === 0;
}

export function mergeOperationsActionPermissions(...values: Array<unknown>): OperationsActionPermissions {
  return values.reduce<OperationsActionPermissions>((acc, value) => {
    const normalizedValue = normalizeOperationsActionPermissions(value);

    Object.entries(normalizedValue.item_types).forEach(([itemType, actionMap]) => {
      acc.item_types[itemType] = {
        ...(acc.item_types[itemType] || {}),
        ...actionMap,
      };
    });

    Object.entries(normalizedValue.workflows ?? {}).forEach(([workflowKey, workflowValue]) => {
      const existingBucket = acc.workflows?.[workflowKey]?.item_types ?? {};
      const mergedBucket: Record<string, OperationsActionMap> = { ...existingBucket };
      Object.entries(workflowValue.item_types).forEach(([itemType, actionMap]) => {
        mergedBucket[itemType] = { ...(mergedBucket[itemType] || {}), ...actionMap };
      });
      acc.workflows = { ...(acc.workflows ?? {}), [workflowKey]: { item_types: mergedBucket } };
    });

    return acc;
  }, { item_types: {} });
}

export function diffOperationsActionPermissions(baseValue: unknown, nextValue: unknown): OperationsActionPermissions | null {
  const basePermissions = normalizeOperationsActionPermissions(baseValue);
  const nextPermissions = normalizeOperationsActionPermissions(nextValue);
  const diff: OperationsActionPermissions = { item_types: {} };

  const itemTypes = new Set([
    ...Object.keys(basePermissions.item_types),
    ...Object.keys(nextPermissions.item_types),
  ]);

  itemTypes.forEach((itemType) => {
    const baseActionMap = basePermissions.item_types[itemType] || {};
    const nextActionMap = nextPermissions.item_types[itemType] || {};

    OPERATIONS_ACTION_KEYS.forEach((actionKey) => {
      if (baseActionMap[actionKey] !== nextActionMap[actionKey] && typeof nextActionMap[actionKey] === 'boolean') {
        diff.item_types[itemType] = {
          ...(diff.item_types[itemType] || {}),
          [actionKey]: nextActionMap[actionKey],
        };
      }
    });
  });

  return isOperationsActionPermissionsEmpty(diff) ? null : diff;
}

export function getOperationsActionPermissionOverride(
  value: unknown,
  itemType: unknown,
  action: OperationsActionKey,
  workflowKey?: string | null,
): boolean | undefined {
  const normalizedPermissions = normalizeOperationsActionPermissions(value);
  const normalizedItemType = normalizeOperationsActionItemType(itemType);
  if (!normalizedItemType) return undefined;

  if (workflowKey) {
    const scoped = normalizedPermissions.workflows?.[workflowKey]?.item_types[normalizedItemType]?.[action];
    if (typeof scoped === 'boolean') return scoped;
  }

  return normalizedPermissions.item_types[normalizedItemType]?.[action];
}

/**
 * Returns true when `itemType` is a configured row in the operations action
 * permissions matrix (i.e. at least one action cell is explicitly set). Used
 * to decide whether a button should be matrix-gated or fall back to legacy
 * feature-flag visibility.
 */
export function hasOperationsActionPermissionRow(
  value: unknown,
  itemType: unknown,
  workflowKey?: string | null,
): boolean {
  const normalizedItemType = normalizeOperationsActionItemType(itemType);
  if (!normalizedItemType) return false;
  const normalizedPermissions = normalizeOperationsActionPermissions(value);
  if (workflowKey
    && normalizedPermissions.workflows?.[workflowKey]?.item_types[normalizedItemType] !== undefined) {
    return true;
  }
  return normalizedPermissions.item_types[normalizedItemType] !== undefined;
}

function getCreateActionDefault(itemType: string, role: string, settings?: Record<string, any> | null): boolean {
  switch (itemType) {
    case 'job':
      return Boolean(settings?.can_create_jobs);
    case 'proposal':
      return Boolean(settings?.smart_task_proposal_creation) || role !== 'user';
    case 'quote':
      return Boolean(settings?.smart_task_quote_creation) || role !== 'user';
    case 'invoice':
      return Boolean(settings?.smart_task_invoice_creation) || role !== 'user';
    case 'expense':
      return Boolean(settings?.smart_task_expense_creation) || role !== 'user';
    case 'credit_note':
      return Boolean(settings?.smart_task_credit_note_creation) || role !== 'user';
    case 'appointment':
      return role !== 'user';
    default:
      return role !== 'user';
  }
}

function getRoleActionDefault(
  role: string,
  itemType: string,
  action: OperationsActionKey,
  settings?: Record<string, any> | null,
): boolean {
  switch (action) {
    case 'smart_task':
      // Smart Task visibility is controlled ONLY by the explicit checkbox in
      // the Actions tab matrix (PermissionsSlideOver). There is no role
      // bypass — admins, owners and root users see a Smart Task button only
      // when the row's smart_task cell is explicitly granted (seeded true by
      // default for workflow groups).
      return false;
    case 'create':
      return getCreateActionDefault(itemType, role, settings);
    case 'resolve':
      if (itemType === 'job') {
        return settings?.can_complete_jobs !== false;
      }
      return true;
    case 'close':
      return isAdminRole(role) || role === 'supervisor';
    case 'admin_reopen':
      return isAdminRole(role);
    case 'reopen':
      return isAdminRole(role) || role === 'supervisor';
    case 'delete':
      return isAdminRole(role);
    case 'print':
      return role !== 'user';
    case 'view':
      // Team member view access: owner/root/admin/supervisor by default
      return isAdminRole(role) || role === 'supervisor';
    case 'edit':
      // Team member edit access: owner/root/admin by default
      return isAdminRole(role);
    case 'edit_comments':
      // Comments are editable by all roles by default (matches the legacy
      // operations_job_edit_work_item_comments feature flag which is true
      // unless explicitly set to false). Admins can restrict via the Actions
      // tab to deny comment editing for specific item types / roles.
      return true;
    case 'edit_timers':
      // Time entries are editable by all roles by default (matches the legacy
      // operations_job_edit_timers_for_completion feature flag which is true
      // unless explicitly set to false). Admins can restrict via the Actions
      // tab to deny timer editing for specific item types / roles.
      return true;
    case 'edit_photos':
      // Photo details (captions, captured times, visibility) are editable by
      // all roles by default (matches the legacy
      // operations_job_edit_field_service_photos feature flag which is true
      // unless explicitly set to false). Admins can restrict via the Actions
      // tab to deny photo editing for specific item types / roles.
      return true;
    case 'submit_for_billing':
      // owner/root/admin/manager can submit for billing
      return isAdminRole(role) || role === 'manager';
    case 'review_billing':
      // owner/root/admin/manager can review billing
      return isAdminRole(role) || role === 'manager';
    case 'approve_billing':
      // Only owner/root/admin can approve billing (Strict mode)
      return isAdminRole(role);
    // ── Workflow-specific action keys: deny by default so the admin
    // must explicitly toggle them on in the Actions tab. ──
    case 'issue_to_van':
    case 'issue_to_job':
    case 'return_to_warehouse':
    case 'receive_stock':
    case 'stock_count':
    case 'manage_equipment':
      return false;
    // ── Fleet management action keys ──
    case 'create_inspection':
      // All roles can complete inspections by default (field workers do inspections)
      return true;
    case 'override_inspection':
      // Only admins can authorize inspection overrides
      return isAdminRole(role);
    case 'change_vehicle':
      // All roles can change vehicle by default
      return true;
    case 'handover_vehicle':
      // All roles can hand over vehicle by default
      return true;
    case 'log_service':
      // All roles can log service entries by default
      return true;
    case 'log_fuel':
      // All roles can log fuel entries by default
      return true;
    case 'assign_driver':
      // Only admins can assign drivers by default
      return isAdminRole(role);
    case 'manage_license':
      // Only admins can manage licenses by default
      return isAdminRole(role);
    case 'acknowledge_inspection':
      // Admins and supervisors can acknowledge inspection reviews
      return isAdminRole(role) || role === 'supervisor';
    case 'run_ai_review':
      // Admins and supervisors can run AI reviews
      return isAdminRole(role) || role === 'supervisor';
    case 'manage_fleet_settings':
      // Only admins can manage fleet settings
      return isAdminRole(role);
    // ── Total Forms action keys ──
    case 'submit':
      // All roles can submit form responses by default
      return true;
    case 'share':
      // All roles can share forms via links/embeds by default
      return true;
    case 'publish':
      // Admins and supervisors can publish forms and templates
      return isAdminRole(role) || role === 'supervisor';
    case 'assign':
      // Only admins can assign forms to clients/sites
      return isAdminRole(role);
    case 'review':
      // Admins and supervisors can review submissions
      return isAdminRole(role) || role === 'supervisor';
    case 'approve':
      // Admins and supervisors can approve requests by default
      return isAdminRole(role) || role === 'supervisor';
    default:
      return false;
  }
}

export function buildDefaultOperationsActionPermissions(
  itemTypes: Array<string | null | undefined> | null | undefined,
  role: string,
  settings?: Record<string, any> | null,
): OperationsActionPermissions {
  const normalizedItemTypes = Array.from(new Set((itemTypes || [])
    .map((itemType) => normalizeOperationsActionItemType(itemType))
    .filter(Boolean)));

  return {
    item_types: normalizedItemTypes.reduce<Record<string, OperationsActionMap>>((acc, itemType) => {
      acc[itemType] = OPERATIONS_ACTION_KEYS.reduce<OperationsActionMap>((actionAcc, actionKey) => {
        actionAcc[actionKey] = getRoleActionDefault(role, itemType, actionKey, settings);
        return actionAcc;
      }, {});
      return acc;
    }, {}),
  };
}

export function getEffectiveOperationsActionPermission(
  settings: Record<string, any> | null | undefined,
  role: string,
  itemType: unknown,
  action: OperationsActionKey,
  workflowKey?: string | null,
): boolean {
  // Guided-tour video capture: surface every action button so tours can
  // demonstrate the full feature surface without seeding per-user grants.
  if (isTourVideoCaptureMode()) return true;

  const explicitOverride = getOperationsActionPermissionOverride(
    settings?.operations_action_permissions, itemType, action, workflowKey);
  if (typeof explicitOverride === 'boolean') {
    return explicitOverride;
  }

  const normalizedItemType = normalizeOperationsActionItemType(itemType);
  return getRoleActionDefault(role, normalizedItemType, action, settings);
}

/**
 * Returns true if the given item type is visible in Smart Task modals for
 * the current user. An item type is visible when the `smart_task` action
 * permission is enabled (either via an explicit group policy override or
 * the role default). When false, the item type is admin-portal-only.
 */
export function isItemTypeVisibleInSmartTask(
  settings: Record<string, any> | null | undefined,
  role: string,
  itemType: unknown,
  workflowKey?: string | null,
): boolean {
  return getEffectiveOperationsActionPermission(settings, role, itemType, 'smart_task', workflowKey);
}

/**
 * Filters a list of item types to only those visible in Smart Task modals.
 * Uses the `smart_task` action permission per item type.
 */
export function filterVisibleSmartTaskItemTypes(
  settings: Record<string, any> | null | undefined,
  role: string,
  itemTypes: Array<string | null | undefined> | null | undefined,
  workflowKey?: string | null,
): string[] {
  return Array.from(new Set((itemTypes || [])
    .map((itemType) => normalizeOperationsActionItemType(itemType))
    .filter(Boolean)))
    .filter((itemType) => isItemTypeVisibleInSmartTask(settings, role, itemType, workflowKey));
}

export function countAllowedOperationsActions(
  settings: Record<string, any> | null | undefined,
  role: string,
  itemTypes: Array<string | null | undefined> | null | undefined,
): number {
  return Array.from(new Set((itemTypes || [])
    .map((itemType) => normalizeOperationsActionItemType(itemType))
    .filter(Boolean)))
    .reduce((count, itemType) => {
      return count + OPERATIONS_ACTION_KEYS.filter((actionKey) => getEffectiveOperationsActionPermission(settings, role, itemType, actionKey)).length;
    }, 0);
}

export function setOperationsActionPermission<T extends Record<string, any>>(
  settings: T,
  itemType: unknown,
  action: OperationsActionKey,
  allowed: boolean,
): T {
  const normalizedItemType = normalizeOperationsActionItemType(itemType);
  if (!normalizedItemType) {
    return settings;
  }

  const nextPermissions = mergeOperationsActionPermissions(settings.operations_action_permissions, {
    item_types: {
      [normalizedItemType]: {
        [action]: allowed,
      },
    },
  });

  return {
    ...settings,
    operations_action_permissions: nextPermissions,
  };
}

/**
 * Overlay a per-solution permission slice (ResolvedPermissions
 * .solutionPermissions[solutionKey]) onto a flat permissions map.
 *
 * Feature keys in the slice replace flat values wholesale. For
 * operations_action_permissions, the slice's item_types rows and
 * workflows[workflowKey] buckets REPLACE the flat rows/buckets they own —
 * a solution's surface is governed only by its own groups, so e.g.
 * security-services' fleet-management matrix never competes with
 * field-services' instance of the same workflow.
 *
 * `options.overridesOnly` (the per-user module_access.custom_permissions
 * portion) outranks every group layer — slice values must never re-impose
 * a group-policy value over a key the user explicitly overrides (e.g.
 * smart_task_non_driver). Both feature keys and matrix cells are protected.
 */
export function overlaySolutionPermissions(
  base: Record<string, any> | null | undefined,
  slice: Record<string, any> | null | undefined,
  options?: { overridesOnly?: Record<string, any> | null },
): Record<string, any> {
  const baseRecord = base ?? {};
  if (!slice || Object.keys(slice).length === 0) {
    return { ...baseRecord };
  }

  const overridesOnly = options?.overridesOnly ?? null;
  const protectedKeys = new Set(
    Object.keys(overridesOnly ?? {}).filter((key) => key !== 'operations_action_permissions'),
  );
  const merged = { ...baseRecord };
  for (const [key, value] of Object.entries(slice)) {
    if (key === 'operations_action_permissions' || protectedKeys.has(key)) continue;
    merged[key] = value;
  }

  const sliceMatrixRaw = slice.operations_action_permissions;
  if (sliceMatrixRaw) {
    const baseMatrix = normalizeOperationsActionPermissions(baseRecord.operations_action_permissions);
    const sliceMatrix = normalizeOperationsActionPermissions(sliceMatrixRaw);
    const overrideMatrix = normalizeOperationsActionPermissions(overridesOnly?.operations_action_permissions);

    const itemTypes = { ...baseMatrix.item_types };
    for (const [itemType, actionMap] of Object.entries(sliceMatrix.item_types)) {
      const overrideCells = overrideMatrix.item_types[itemType];
      itemTypes[itemType] = overrideCells ? { ...actionMap, ...overrideCells } : actionMap;
    }

    const workflows: Record<string, { item_types: Record<string, OperationsActionMap> }> = {
      ...(baseMatrix.workflows ?? {}),
    };
    for (const [workflowKey, bucket] of Object.entries(sliceMatrix.workflows ?? {})) {
      const overrideBucket = overrideMatrix.workflows?.[workflowKey];
      if (!overrideBucket) {
        workflows[workflowKey] = bucket;
        continue;
      }
      const mergedItemTypes = { ...(bucket.item_types ?? {}) };
      for (const [itemType, actionMap] of Object.entries(overrideBucket.item_types ?? {})) {
        mergedItemTypes[itemType] = { ...(mergedItemTypes[itemType] ?? {}), ...actionMap };
      }
      workflows[workflowKey] = { item_types: mergedItemTypes };
    }

    merged.operations_action_permissions = { item_types: itemTypes, workflows };
  }

  return merged;
}

export function mergeSettingsWithOperationsActionPermissions<T extends Record<string, any>>(
  ...values: Array<T | null | undefined>
): T {
  let mergedSettings = {} as T;
  let mergedPermissions: OperationsActionPermissions | null = null;

  values.forEach((value) => {
    const plainValue = toPlainObject<Record<string, any>>(value);
    if (!plainValue) {
      return;
    }

    const { operations_action_permissions, ...rest } = plainValue;
    mergedSettings = {
      ...mergedSettings,
      ...(rest as T),
    };

    if (operations_action_permissions !== undefined) {
      mergedPermissions = mergeOperationsActionPermissions(mergedPermissions, operations_action_permissions);
    }
  });

  if (mergedPermissions && !isOperationsActionPermissionsEmpty(mergedPermissions)) {
    (mergedSettings as Record<string, any>).operations_action_permissions = mergedPermissions;
  }

  return mergedSettings;
}

export function stripOperationsActionPermissions<T extends Record<string, any>>(settings: T): T {
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
    return settings;
  }

  const { operations_action_permissions, ...rest } = settings;
  return rest as T;
}

const RESOLVED_STATUSES = new Set(['resolved']);
const CLOSED_STATUSES = new Set(['closed', 'completed', 'done', 'finished']);
const TERMINAL_STATUSES = new Set([...RESOLVED_STATUSES, ...CLOSED_STATUSES, 'cancelled']);
const ADMIN_REOPEN_SOURCE_STATUSES = new Set(['closed', 'missed']);
const ADMIN_REOPEN_TARGET_STATUS = 'admin_reopened';

function normalizeStatusToken(status: unknown): string {
  return typeof status === 'string'
    ? status.trim().toLowerCase().replace(/[\s-]+/g, '_')
    : '';
}

// ── Status-level permissions ──

export type OperationsStatusPermissionMap = Record<string, boolean>;
export type OperationsStatusPermissions = Record<string, OperationsStatusPermissionMap>;

function normalizeStatusKey(status: unknown): string {
  return typeof status === 'string'
    ? status.trim().toLowerCase().replace(/[\s-]+/g, '_')
    : '';
}

export function normalizeOperationsStatusPermissions(value: unknown): OperationsStatusPermissions {
  const raw = toPlainObject<Record<string, unknown>>(value);
  if (!raw) return {};

  const result: OperationsStatusPermissions = {};
  for (const [rawItemType, rawStatusMap] of Object.entries(raw)) {
    const itemType = normalizeOperationsActionItemType(rawItemType);
    const statusMap = toPlainObject<Record<string, unknown>>(rawStatusMap);
    if (!itemType || !statusMap) continue;

    const normalized: OperationsStatusPermissionMap = {};
    for (const [rawStatus, rawAllowed] of Object.entries(statusMap)) {
      const statusKey = normalizeStatusKey(rawStatus);
      if (statusKey && typeof rawAllowed === 'boolean') {
        normalized[statusKey] = rawAllowed;
      }
    }
    if (Object.keys(normalized).length > 0) {
      result[itemType] = normalized;
    }
  }
  return result;
}

export function getOperationsStatusPermissionOverride(
  value: unknown,
  itemType: unknown,
  statusKey: string,
): boolean | undefined {
  const normalized = normalizeOperationsStatusPermissions(value);
  const normalizedItemType = normalizeOperationsActionItemType(itemType);
  const normalizedStatus = normalizeStatusKey(statusKey);
  if (!normalizedItemType || !normalizedStatus) return undefined;
  return normalized[normalizedItemType]?.[normalizedStatus];
}

export function getEffectiveOperationsStatusPermission(
  settings: Record<string, unknown> | null | undefined,
  role: string,
  itemType: unknown,
  statusKey: string,
): boolean {
  const override = getOperationsStatusPermissionOverride(
    settings?.operations_status_permissions,
    itemType,
    statusKey,
  );
  if (typeof override === 'boolean') {
    return override;
  }
  // Default: all statuses allowed for all roles
  return true;
}

export function setOperationsStatusPermission<T extends Record<string, unknown>>(
  settings: T,
  itemType: unknown,
  statusKey: string,
  allowed: boolean,
): T {
  const normalizedItemType = normalizeOperationsActionItemType(itemType);
  const normalizedStatus = normalizeStatusKey(statusKey);
  if (!normalizedItemType || !normalizedStatus) {
    return settings;
  }

  const current = normalizeOperationsStatusPermissions(settings.operations_status_permissions);
  const itemMap = { ...(current[normalizedItemType] || {}) };
  itemMap[normalizedStatus] = allowed;

  const next: OperationsStatusPermissions = {
    ...current,
    [normalizedItemType]: itemMap,
  };

  return {
    ...settings,
    operations_status_permissions: next,
  };
}

export function getOperationsActionForStatusChange(
  currentStatus: unknown,
  nextStatus: unknown,
): OperationsActionKey | null {
  const current = normalizeStatusToken(currentStatus);
  const next = normalizeStatusToken(nextStatus);

  if (!next || current === next) {
    return null;
  }

  if (RESOLVED_STATUSES.has(next) && !RESOLVED_STATUSES.has(current)) {
    return 'resolve';
  }

  if (CLOSED_STATUSES.has(next) && !CLOSED_STATUSES.has(current)) {
    return 'close';
  }

  if (ADMIN_REOPEN_SOURCE_STATUSES.has(current) && next === ADMIN_REOPEN_TARGET_STATUS) {
    return 'admin_reopen';
  }

  if (current === ADMIN_REOPEN_TARGET_STATUS && next === 'closed') {
    return 'admin_reopen';
  }

  if (TERMINAL_STATUSES.has(current) && !TERMINAL_STATUSES.has(next)) {
    return 'reopen';
  }

  return null;
}
